/**
 * Scheduled Delivery Component
 * Allows customers to pre-book delivery slots up to 7 days in advance
 */
import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Check, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import api from '../lib/api';
import { toast } from 'sonner';

const ScheduledDelivery = ({ 
  onSlotSelect, 
  selectedSlot,
  parish,
  className = ''
}) => {
  const [loading, setLoading] = useState(true);
  const [availableDays, setAvailableDays] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlotId, setSelectedSlotId] = useState(null);

  useEffect(() => {
    fetchAvailableSlots();
  }, [parish]);

  const fetchAvailableSlots = async () => {
    try {
      setLoading(true);
      const response = await api.get('/delivery-slots/available', {
        params: { parish, days: 7 }
      });
      setAvailableDays(response.data.days || []);
      
      const firstAvailable = response.data.days?.find(d => d.is_available);
      if (firstAvailable) {
        setSelectedDate(firstAvailable.date);
      }
    } catch (error) {
      console.error('Error fetching slots:', error);
      toast.error('Failed to load delivery slots');
    } finally {
      setLoading(false);
    }
  };

  const handleSlotSelect = (date, slot) => {
    if (!slot.is_available) return;
    
    setSelectedDate(date);
    setSelectedSlotId(slot.slot_id);
    
    if (onSlotSelect) {
      onSlotSelect({
        date,
        slot_id: slot.slot_id,
        slot_label: slot.label,
        time_range: slot.time_range,
        premium_fee: slot.premium_fee
      });
    }
  };

  const getDateButtonClass = (day, isSelected) => {
    if (isSelected) return 'border-emerald-500 bg-emerald-50';
    if (day.is_available) return 'border-gray-200 hover:border-emerald-300';
    return 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed';
  };

  const getSlotButtonClass = (slot, isSlotSelected) => {
    if (!slot.is_available) return 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200';
    if (isSlotSelected) return 'bg-emerald-500 text-white border-emerald-500 ring-2 ring-emerald-300';
    if (slot.premium_fee > 0) return 'bg-amber-50 hover:bg-amber-100 border-amber-200 text-amber-800';
    return 'bg-white hover:bg-emerald-50 border-emerald-200';
  };

  const selectedDayData = availableDays.find(d => d.date === selectedDate);

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
            <span className="ml-3 text-gray-500">Loading delivery slots...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Calendar className="h-5 w-5 text-emerald-500" />
          Schedule Your Delivery
        </CardTitle>
        <p className="text-sm text-gray-500">
          Pre-book a delivery slot up to 7 days in advance
        </p>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Date Selection */}
        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 block">
            Select Delivery Date
          </label>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {availableDays.map((day) => {
              const isSelected = selectedDate === day.date;
              const dateObj = new Date(day.date + 'T00:00:00');
              
              return (
                <button
                  key={day.date}
                  onClick={() => day.is_available && setSelectedDate(day.date)}
                  disabled={!day.is_available}
                  className={`flex-shrink-0 px-4 py-3 rounded-lg border-2 transition-all ${getDateButtonClass(day, isSelected)}`}
                >
                  <div className="text-xs text-gray-500">
                    {dateObj.toLocaleDateString('en-US', { weekday: 'short' })}
                  </div>
                  <div className={`text-lg font-bold ${isSelected ? 'text-emerald-600' : 'text-gray-800'}`}>
                    {dateObj.getDate()}
                  </div>
                  <div className="text-xs text-gray-500">
                    {dateObj.toLocaleDateString('en-US', { month: 'short' })}
                  </div>
                  {!day.is_available && (
                    <Badge variant="secondary" className="mt-1 text-xs">N/A</Badge>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Time Slot Selection */}
        {selectedDate && selectedDayData && (
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Select Time Slot
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {selectedDayData.slots.map((slot) => {
                const isSlotSelected = selectedSlotId === slot.slot_id;
                
                return (
                  <button
                    key={slot.slot_id}
                    onClick={() => handleSlotSelect(selectedDate, slot)}
                    disabled={!slot.is_available}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${getSlotButtonClass(slot, isSlotSelected)}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        <span className="font-medium">{slot.label}</span>
                      </div>
                      {isSlotSelected && <Check className="h-5 w-5" />}
                    </div>
                    <div className="text-sm mt-1 opacity-80">{slot.time_range}</div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs">
                        {slot.is_available ? `${slot.available} slots left` : 'Fully booked'}
                      </span>
                      {slot.premium_fee > 0 && (
                        <Badge variant="outline" className="text-xs bg-amber-50 border-amber-300 text-amber-700">
                          +J${slot.premium_fee.toLocaleString()}
                        </Badge>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Selected Slot Summary */}
        {selectedSlot && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-emerald-700">
              <Check className="h-5 w-5" />
              <span className="font-medium">Scheduled Delivery Selected</span>
            </div>
            <div className="mt-2 text-sm text-emerald-600">
              <p><strong>Date:</strong> {new Date(selectedSlot.date + 'T00:00:00').toLocaleDateString('en-US', { 
                weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
              })}</p>
              <p><strong>Time:</strong> {selectedSlot.time_range}</p>
              {selectedSlot.premium_fee > 0 && (
                <p><strong>Premium Fee:</strong> J${selectedSlot.premium_fee.toLocaleString()}</p>
              )}
            </div>
          </div>
        )}

        {/* Info Notice */}
        <div className="flex items-start gap-2 text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <p>
            Scheduled delivery slots are reserved for 30 minutes. Evening and express slots have a premium fee.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default ScheduledDelivery;
